<template>
  <v-container fluid class="text-xs-justify">
    <!-- intro ~*~*~*~*~*~*~*~*~*~*~*~*~*~* -->

    <CorePageIntro>
      <template slot="content">
        This master list of constraints is used as the base for all facilities.
        Any changes made here <strong>will be reflected in every facility that is 
        subscribed to that constraint</strong>, so be confident you want to change any constraint data.
        &nbsp;&nbsp;&nbsp;Click
        <v-icon small color="orange darken-2">mdi-plus-circle-outline</v-icon> to add, 
        <v-icon small color="primary">edit</v-icon> to modify.
      </template>
    </CorePageIntro>

    <v-card outlined>
      <v-card-text>
        <!-- top header / toolbar ~*~*~*~*~*~*~*~*~*~*~*~*~*~* -->
        <v-toolbar flat>
          <v-toolbar-title class="text-uppercase body-1 font-weight-medium">Master Constraints</v-toolbar-title>
          <v-divider class="mx-4" inset vertical></v-divider>

          <!-- new constraint dialog ~*~*~*~*~*~*~*~*~*~*~*~*~*~* -->
          <v-dialog v-model="dialog" max-width="500px">
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" fab small text>
                <v-icon large color="orange darken-2 ml-5">mdi-plus-circle-outline</v-icon>
              </v-btn>
            </template>
            <v-card outlined>
              <v-card-title>
                <span class="headline">{{ formTitle }}</span>
              </v-card-title>
              <v-form  ref="form" v-model="valid">
                <v-card-text>
                  <v-container grid-list-md>
                    <v-layout wrap>
                      <v-flex xs11 text-left>
                        <v-text-field
                          :rules="[v => !!v || 'Name is required']"
                          v-model.trim="editedItem.name"
                          label="Constraint Name"
                          required
                        ></v-text-field>
                      </v-flex>

                      <v-flex xs11 text-left>
                        <v-text-field
                          :rules="[v => !!v || 'Description is required']"
                          v-model.trim="editedItem.description"
                          label="Description"
                          required
                        ></v-text-field>
                      </v-flex>

                      <v-flex xs12 sm5 text-left>
                        <v-select
                          :items="[
                            'COGEN',
                            'UTILITY',
                            'ISO',
                            'GENERAL'
                          ]"
                          :rules="[v => !!v || 'Type is required']"
                          v-model="editedItem.constraint_type"
                          label="Constraint Type"
                          required
                        ></v-select>
                      </v-flex>
                      <v-flex xs12 sm5 text-left>
                        <v-select
                          :items="[
                            'INTEGER',
                            'FLOAT',
                            'STRING',
                            'BOOLEAN'
                          ]"
                          :rules="[v => !!v || 'Type is required']"
                          v-model="editedItem.data_type"
                          label="Data Type"
                          required
                        ></v-select>
                      </v-flex>

                      <v-flex xs12 sm4 text-left>
                        <v-text-field
                          :rules="[ v => !!v || 'Default value is required' ]"
                          v-model="editedItem.default_value"
                          label="Default Value"
                          required
                        ></v-text-field>
                      </v-flex>
                      <v-flex xs12 sm4 text-left>
                        <v-text-field
                          :rules="[ v => !!v || 'Minimum value is required' ]"
                          v-model="editedItem.min_value"
                          label="Minimum Value"
                          required
                        ></v-text-field>
                      </v-flex>
                      <v-flex xs12 sm4 text-left>
                        <v-text-field
                          :rules="[ v => !!v || 'Maximum value is required' ]"
                          v-model="editedItem.max_value"
                          label="Maximum Value"
                          required
                        ></v-text-field>
                      </v-flex>

                      <v-flex xs12 sm4 text-left>
                        <v-text-field
                          v-model="editedItem.unit"
                          label="Unit"
                        ></v-text-field>
                      </v-flex>

                      <v-flex xs12 sm4 text-left>
                        <v-checkbox
                          v-model="editedItem.modifiable"
                          label="Modifiable"
                        ></v-checkbox>
                      </v-flex>
                    </v-layout>
                  </v-container>
                </v-card-text>
              </v-form>

              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn @click.native="close" small color="primary" text>Cancel</v-btn>
                <v-btn @click.native="save" small color="primary" xt>Save</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <div class="flex-grow-1"></div>

          <!-- search ~*~*~*~*~*~*~*~*~*~*~*~*~*~* -->
          <v-col cols="3" sm="3" md="3">
            <v-text-field
                v-model="search"
                append-icon="search"
                label="Search"
                single-line
                hide-details
                ></v-text-field>
          </v-col>

        </v-toolbar>

        <v-data-table
          :headers="headers"
          :items="constraints"
          :search="search"
          :sort-by="['constraint_type', 'name']"
          :sort-desc="[false, false]"
          :items-per-page="15"
          :key="`measter-table-${componentKey}`"
          v-model="constraints"
          item-key="name"
          multi-sort
          dense
        >
          <template v-slot:top>
            <p>&nbsp;</p>
          </template>

          <template v-slot:item.action="{ item }">
            <v-icon @click="editItem(item)" color="primary">
              edit
            </v-icon>
          </template>

          <template v-slot:no-data>
            No constaints found.
          </template>

          <template v-slot:item.modifiable="{ item }">
              <!-- {{ item.modifiable }}
              <span v-if="item.modifiable">YES</span>
              <span v-else>NO</span> -->
              <v-chip v-if="item.modifiable" small color="green" dark><span class="overline">YES</span></v-chip>
              <v-chip v-else                         small color="red" dark><span class="overline">NO</span></v-chip>
          </template>

          <!-- <template v-slot:item.modifiable="{ item }">
            <v-chip v-if="item.modifiable === true" small color="green" dark><span class="overline">YES</span></v-chip>
            <v-chip v-else                         small color="red" dark><span class="overline">NO</span></v-chip>
          </template> -->

        </v-data-table>
      </v-card-text>
    </v-card>

  </v-container>
</template>

<script>
import ConstraintService from '@/services/ConstraintService'
export default {
  name: 'MasterConstraints',
  data() {
    return {
      constraints: [],
      error: false,
      search: '',
      dialog: false,
      componentKey: 0,
      headers: [
        { text: 'TYPE',       align: 'left',   sortable: true,  value: 'constraint_type' },
        { text: 'NAME',       align: 'left',   sortable: true,  value: 'name'            },
        { text: 'DEFAULT',    align: 'right',  sortable: true,  value: 'default_value'   },
        { text: 'MIN',        align: 'right',  sortable: true,  value: 'min_value'       },
        { text: 'MAX',        align: 'right',  sortable: true,  value: 'max_value'       },
        { text: 'UNIT',       align: 'right',  sortable: true,  value: 'unit'            },
        { text: 'MODIFIABLE', align: 'center', sortable: false, value: 'modifiable'       },
        { text: 'Actions',    align: 'center', sortable: false, value: 'action'          } 
      ],
      editedIndex: -1,
      editedItem: {
        master_constraint_id: null,
        constraint_type: '',
        data_type: '',
        name: '',
        description: '',
        default_value: '',
        min_value: '',
        max_value: '',
        unit: '',
        modifiable: ''
      },
      defaultItem: {
        master_constraint_id: null,
        constraint_type: 'FACILITY',
        data_type: 'INTEGER',
        name: '',
        description: '',
        default_value: '',
        min_value: '',
        max_value: '',
        unit: '',
        modifiable: 'true'
      },
      valid: false,
      nameRules: [
        v => !!v || 'Name is required',
        v => (v && v.length <= 10) || 'Name must be less than 10 characters'
      ]
    }
  },
  computed: {
    formTitle() {
      return this.editedIndex === -1 ? 'New Constraint' : 'Edit Constraint'
    }
  },
  created() {
    this.init()
    this.fetch()
  },
  methods: {
    init() {
      this.editedItem = Object.assign({}, this.defaultItem)
      this.editedIndex = -1
    },
    fetch() {
      this.constraints = []
      ConstraintService.getConstraints()
        .then(response => { 
          // this.constraints = response.data
          this.constraints = []
          for (var c = 0; c < response.data.length; c++) {
            this.constraints.push(response.data[c])
          }
        })
        .catch(error => { this.$toast.error("Error loading  constraint data. " + error.message) })
    },
    editItem(item) {
      this.editedIndex = this.constraints.indexOf(item)
      this.editedItem = Object.assign({}, item)
      this.dialog = true
    },

    close() {
      this.dialog = false
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      }, 300)
    },

    save() {
      if (this.$refs.form.validate()) {    
        ConstraintService.postConstraint(this.editedItem)
          .then(response => {
              this.$toast.success("Constaint " + this.editedItem.name + " saved.") 

              // refresh the list
              if (this.editedIndex > -1) {
                Object.assign(this.constraints[this.editedIndex], this.editedItem)
              } else {
                this.constraints.push(this.editedItem)
              }
          })
          .catch(error => { this.$toast.error("Error saving constraint data. " + error.message) })

          this.close()
          this.componentKey += 1

      }
    }
  }
}
</script>
